import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  WorkflowPlayer,
  type WorkflowCompletionPayload,
} from '@little-loop/tutorial-player';

import {
  completeShareLink,
  lookupShareLink,
  type LookupErrorCode,
  type LookupResult,
} from '../api/shareLink';

/**
 * Where caregivers are pointed when they need help. One address across every
 * app and site — there is no `support@` alias.
 */
const SUPPORT_EMAIL = 'hello@littleloop.care';

type ScreenState =
  | { kind: 'loading' }
  | { kind: 'error'; code: LookupErrorCode | 'network'; expiresAt?: string }
  | { kind: 'ready'; data: Extract<LookupResult, { valid: true }> }
  | { kind: 'success'; tutorialTitle: string; guardianName: string };

export default function ShareLinkRoute() {
  const { token } = useParams<{ token: string }>();
  const [screen, setScreen] = useState<ScreenState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setScreen({ kind: 'error', code: 'invalid_format' });
      return;
    }
    (async () => {
      try {
        const result = await lookupShareLink(token);
        if (cancelled) return;
        if (!result.valid) {
          setScreen({
            kind: 'error',
            code: result.error,
            ...(result.expires_at !== undefined ? { expiresAt: result.expires_at } : {}),
          });
        } else {
          setScreen({ kind: 'ready', data: result });
        }
      } catch (err) {
        console.error('[share-link-lookup] failed', err);
        if (!cancelled) setScreen({ kind: 'error', code: 'network' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleComplete = useCallback(
    async (payload: WorkflowCompletionPayload) => {
      if (!token || screen.kind !== 'ready') return;
      try {
        // ENG-175: the share path now carries the play. `visits` is node-keyed;
        // map each visited CARD node to its card_id (via the graph) for the
        // card-keyed card_progress. Session values are stringified with the
        // engine's rule (booleans as true/false, numbers via String — no ".0"),
        // matching buildFacts so the recorded value equals what drove routing.
        const tutorial = screen.data.tutorial;
        const nodeToCard = new Map<string, string>();
        for (const n of tutorial.graph.nodes) {
          if (n.type === 'card') nodeToCard.set(n.id, n.card_id);
        }
        const card_progress = Object.entries(payload.visits)
          .filter(([nodeId]) => nodeToCard.has(nodeId))
          .map(([nodeId, count]) => ({ card_id: nodeToCard.get(nodeId)!, view_count: count }));
        const session_values = Object.fromEntries(
          Object.entries(payload.sessionValues).map(([k, v]) => [
            k,
            typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v),
          ]),
        );

        await completeShareLink({
          token,
          completion_time_seconds: payload.durationSeconds,
          branch_path: payload.path,
          responses: payload.answers,
          card_progress,
          session_values,
          restarts: payload.restarts,
          outcome_code: payload.outcomeCode,
          total_cards: Object.keys(tutorial.cards).length,
          workflow_version_id: payload.workflowVersionId,
        });
      } catch (err) {
        // Non-fatal — the player has already shown the terminal card. The
        // guardian just won't receive the push notification this round.
        console.error('[share-link-complete] failed', err);
      }
      setScreen({
        kind: 'success',
        tutorialTitle: screen.data.tutorial.title,
        guardianName: screen.data.manager_name,
      });
    },
    [token, screen],
  );

  return (
    <main className="mx-auto flex h-dvh max-w-md flex-col overflow-hidden px-4 sm:max-w-2xl sm:px-6">
      {screen.kind === 'loading' ? <LoadingState /> : null}
      {screen.kind === 'error' ? (
        <ErrorState code={screen.code} expiresAt={screen.expiresAt} />
      ) : null}
      {screen.kind === 'ready' ? (
        <ReadyState data={screen.data} onComplete={handleComplete} />
      ) : null}
      {screen.kind === 'success' ? (
        <SuccessState tutorialTitle={screen.tutorialTitle} guardianName={screen.guardianName} />
      ) : null}
    </main>
  );
}

// ── Brand chrome ────────────────────────────────────────────────────────────

/** The Little Loop icon + wordmark lockup, reused across screens. */
function BrandRow({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Logo />
      <span className="text-xs font-semibold uppercase tracking-wider text-teal-700">
        Little Loop
      </span>
    </div>
  );
}

/**
 * Player header: brand lockup on the left, the tutorial + dependent name on
 * the right, aligned on one row. The progress bar renders directly beneath it
 * (inside the player).
 */
function PlayerTopBar({
  tutorialTitle,
  dependentName,
}: {
  tutorialTitle: string;
  dependentName: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 pt-6 sm:pt-10">
      <BrandRow className="shrink-0" />
      <div className="flex min-w-0 flex-col items-end text-right">
        <span className="max-w-[55vw] truncate text-sm font-semibold text-slate-900">
          {tutorialTitle}
        </span>
        <span className="truncate text-xs text-slate-500">{dependentName}</span>
      </div>
    </div>
  );
}

// ── Screens ───────────────────────────────────────────────────────────────

/**
 * Pre-roll screen shown before the tutorial starts. Carries the full context
 * (who shared it, for whom, what it is, when it expires) plus a Start button
 * and a support contact.
 */
function WelcomeScreen({
  data,
  onStart,
}: {
  data: Extract<LookupResult, { valid: true }>;
  onStart: () => void;
}) {
  const { tutorial } = data;
  return (
    <motion.div
      className="flex min-h-0 flex-1 flex-col"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <BrandRow className="pt-6 sm:pt-10" />

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto py-8">
        <div className="my-auto flex flex-col gap-4">
          <p className="text-base text-slate-600">
            <span className="font-semibold text-slate-900">{data.manager_name}</span> shared this
            with you to help with{' '}
            <span className="font-semibold text-slate-900">{data.recipient_first_name}</span>.
          </p>
          <h1 className="text-3xl font-bold leading-tight text-slate-900">{tutorial.title}</h1>
          {tutorial.description ? (
            <p className="text-base leading-relaxed text-slate-500">{tutorial.description}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span>Expires {formatDate(data.expires_at)}</span>
            {data.already_completed ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700">
                ✓ Already completed
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-100 pb-[max(env(safe-area-inset-bottom),12px)] pt-4">
        <button
          type="button"
          onClick={onStart}
          className="flex h-12 w-full items-center justify-center rounded-full bg-teal-500 text-base font-semibold text-white shadow-sm transition hover:bg-teal-600"
        >
          Start
        </button>
        <p className="text-center text-xs text-slate-400">
          Need help?{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium text-teal-600 underline underline-offset-2"
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </motion.div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3">
      <motion.div
        className="h-8 w-8 rounded-full border-2 border-teal-500/30 border-t-teal-500"
        animate={{ rotate: 360 }}
        transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
        aria-label="Loading"
      />
      <p className="text-sm text-slate-500">Loading your tutorial…</p>
    </div>
  );
}

function ErrorState({
  code,
  expiresAt,
}: {
  code: LookupErrorCode | 'network';
  expiresAt?: string | undefined;
}) {
  const { title, body } = errorCopy(code, expiresAt);
  return (
    <motion.div
      className="flex flex-1 flex-col items-center justify-center gap-4 px-2 text-center"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-2xl">
        ⏳
      </div>
      <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
      <p className="max-w-sm text-sm text-slate-600">{body}</p>
    </motion.div>
  );
}

function ReadyState({
  data,
  onComplete,
}: {
  data: Extract<LookupResult, { valid: true }>;
  onComplete: (payload: WorkflowCompletionPayload) => void;
}) {
  const [started, setStarted] = useState(false);

  // Build the outcomes map the player needs to render the end screen
  // with author-configured emoji / label / message / color. The shape
  // (Record<code, …>) matches `TerminalOutcomeMap` exported by
  // @little-loop/tutorial-player.
  const outcomes = useMemo(() => {
    const map: Record<string, {
      kind: 'success' | 'failure' | 'neutral';
      label: string;
      emoji: string | null;
      display_message: string | null;
      color_hex: string | null;
    }> = {};
    for (const o of data.tutorial.outcomes ?? []) {
      map[o.code] = {
        kind: o.kind,
        label: o.label,
        emoji: o.emoji,
        display_message: o.display_message,
        color_hex: o.color_hex,
      };
    }
    return map;
  }, [data.tutorial.outcomes]);

  if (!started) {
    return <WelcomeScreen data={data} onStart={() => setStarted(true)} />;
  }

  return (
    <WorkflowPlayer
      header={
        <PlayerTopBar
          tutorialTitle={data.tutorial.title}
          dependentName={data.recipient_first_name}
        />
      }
      graph={data.tutorial.graph}
      cards={data.tutorial.cards}
      workflowVersionId={data.tutorial.workflow_version_id}
      sessionVariables={data.tutorial.session_variables}
      // Routing and block visibility both evaluate against the resolver's
      // recipient facts; interpolation uses the resolved canonical values.
      facts={data.tutorial.facts}
      variableValues={data.tutorial.variable_values}
      outcomes={outcomes}
      mode="play"
      onComplete={onComplete}
    />
  );
}

function SuccessState({
  tutorialTitle,
  guardianName,
}: {
  tutorialTitle: string;
  guardianName: string;
}) {
  return (
    <motion.div
      className="flex flex-1 flex-col items-center justify-center gap-5 px-2 text-center"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-teal-100 text-3xl text-teal-700" aria-hidden="true">
        ✓
      </div>
      <h1 className="text-2xl font-bold text-slate-900">Thanks for taking a moment.</h1>
      <p className="max-w-sm text-sm text-slate-600">
        You finished <span className="font-semibold text-slate-900">{tutorialTitle}</span>. We let{' '}
        {guardianName} know.
      </p>
    </motion.div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function Logo() {
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-500 text-white">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    </span>
  );
}

function errorCopy(
  code: LookupErrorCode | 'network',
  expiresAt: string | undefined,
): { title: string; body: string } {
  switch (code) {
    case 'expired':
      return {
        title: 'This link has expired.',
        body: expiresAt
          ? `It expired on ${formatDate(expiresAt)}. Ask the guardian for a new link.`
          : 'Ask the guardian for a new link.',
      };
    case 'revoked':
      return {
        title: 'This link was revoked.',
        body: 'The guardian cancelled this share. Ask them for a new link if you still need it.',
      };
    case 'not_found':
    case 'invalid_format':
      return {
        title: "We can't find that link.",
        body: 'Double-check the URL or ask the guardian to send a new one.',
      };
    case 'rate_limited':
      return {
        title: 'Too many tries.',
        body: 'You\'ve checked this link too many times in a short window. Try again in a few minutes.',
      };
    case 'tutorial_unavailable':
    case 'tutorial_not_found':
      return {
        title: 'This tutorial is unavailable.',
        body: 'It may have been taken down after the link was sent. Ask the guardian to share something else.',
      };
    case 'tutorial_blocked':
      // The safety canary. Content that is being withheld says so out loud —
      // a caregiver must never be shown a silently shorter tutorial.
      return {
        title: 'This tutorial is on hold.',
        body: 'Part of it is still being safety-reviewed for this child, so we\'re not showing it yet. Ask the guardian to check back shortly.',
      };
    case 'unsupported_scope':
      return {
        title: 'This share isn\'t supported.',
        body: 'The guardian shared something this page can\'t open yet. Ask them to share an individual tutorial instead.',
      };
    case 'network':
      return {
        title: 'Connection trouble.',
        body: 'Check your internet and refresh this page.',
      };
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
